import { supabase } from '@/lib/supabase'
import type { Role } from '@/lib/types'
import { validateCapability } from './permissions'

export type EntryType     = 'receivable' | 'payable'
export type EntryStatus   = 'open' | 'paid' | 'canceled'
export const PAYMENT_METHODS = ['cash', 'pix', 'direct_debit', 'dda', 'card', 'deposit', 'boleto'] as const
export type PaymentMethod = typeof PAYMENT_METHODS[number]

export interface FinancialEntry {
  id:                              string
  unit_id:                         string
  parent_id:                       string | null
  recurrence_id:                   string | null
  entry_type:                      EntryType
  description:                     string
  amount:                          number
  due_date:                        string   // 'YYYY-MM-DD'
  /** If the entry had its due_date postponed, the original date is kept here */
  original_due_date?:              string | null
  status:                          EntryStatus
  party_id:                        string | null
  party_name_snapshot:             string | null
  party_whatsapp_snapshot:         string | null
  party_pix_key_snapshot:          string | null
  payment_method_snapshot:         PaymentMethod | null
  payment_method_details_snapshot: Record<string, unknown> | null
  notes:                           string | null
  paid_at:                         string | null
  receipt_url:                     string | null
  notify_on_due?:                   boolean | null
  /** Time of day to send notification (HH:MM) */
  notify_time?:                     string | null
  /** Minutes before due_date when notification should fire */
  notify_offset_minutes?:           number | null
  recurrence_template_id:          string | null
  created_by:                      string | null
  created_at:                      string
  updated_at:                      string
}

// ─── Recurrence template types ───────────────────────────────────────────────

export type RecurrencePeriodicity = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'installment'
export type RecurrenceStatus      = 'active' | 'terminated'

/** Advanced scheduling options stored as JSON in financial_entry_recurrences.rules_json */
export interface RecurrenceRules {
  weekday_lock?:        boolean
  weekday_lock_day?:    number      // 0=Sun … 6=Sat
  weekend_adjust?:      'none' | 'next' | 'prev'
  weekly_dow?:          number      // 0=Sun … 6=Sat (for weekly periodicity)
  monthly_target_day?:  number      // 1-31 for monthly: which day to use (e.g., always 5th, handles short months by clamping to last day)
  installment_total?:   number      // total parcels (for installment periodicity)
  installment_current_start?: number // which parcel is the first one (1-based; for installment periodicity)
  daily_weekdays?:      number[]    // [0-6] allowed weekdays for daily periodicity (0=Sun … 6=Sat)
}

export interface RecurrenceTemplate {
  id:               string
  unit_id:          string
  origin_entry_id:  string
  periodicity:      RecurrencePeriodicity
  rules_json:       RecurrenceRules
  start_date:       string   // 'YYYY-MM-DD'
  end_date:         string | null
  origin_amount:    number   // The original amount (used for virtual entries; not affected by partial payments)
  status:           RecurrenceStatus
  created_by:       string | null
  created_at:       string
  updated_at:       string
}

export interface RecurrenceTemplateInput {
  unit_id:          string
  origin_entry_id:  string
  periodicity:      RecurrencePeriodicity
  rules_json?:      RecurrenceRules
  start_date:       string
  origin_amount?:   number   // Optional; defaults to origin entry's amount
}

/**
 * A virtual entry is a projected occurrence of a recurrence template that has
 * not yet been materialised as a real database row. It carries all display
 * fields copied from the origin entry plus flags that identify it as virtual.
 */
export interface VirtualEntry extends Omit<FinancialEntry, 'id' | 'created_at' | 'updated_at'> {
  /** Unique client-side identifier (never persisted). Format: "virtual-<templateId>-<due_date>" */
  id:               string
  isVirtual:        true
  recurrence_template_id: string   // always set on virtual entries
  /** The full template, available for materialisation. */
  _template:        RecurrenceTemplate
  created_at:       string
  updated_at:       string
}

/** Union type — both real and virtual entries can flow through the UI. */
export type AnyEntry = FinancialEntry | VirtualEntry

export type ContactType = 'customer' | 'supplier' | 'both'

export interface Party {
  id:           string
  unit_id:      string
  name:         string
  whatsapp:    string | null
  pix_key:     string | null
  notes:       string | null
  party_type:  ContactType | null
  score:       'A' | 'B' | 'C' | 'D' | 'E' | null
  is_active:    boolean
  document_url: string | null
  document_name: string | null
  created_by:   string | null
  created_at:   string
  updated_at:   string
}

export interface PartyInput {
  name:      string
  whatsapp?: string
  pix_key?:  string
  notes?:    string
  is_active?: boolean
}

export interface InactivatePartyResult {
  success: boolean
  partyId: string
  partyName: string
  wasInactivated: boolean  // false if already inactive
  hasOpenEntries: boolean
  openReceivableCount?: number
  openReceivableTotal?: number
  openPayableCount?: number
  openPayableTotal?: number
  errorMessage?: string
}

export interface PartyStats {
  score_pct:           number | null  // computed client-side for display only
  first_paid_at:       string | null
  last_paid_at:        string | null
  overdue_count:       number
  overdue_total:       number
  // Overdue split by entry type
  overdue_receivable_count: number
  overdue_receivable_total: number
  overdue_payable_count:    number
  overdue_payable_total:    number
  // Totals by entry type (all statuses except canceled)
  receivable_count: number
  receivable_total: number
  payable_count:    number
  payable_total:    number
  due_in_7_days_count: number
  due_in_7_days_total: number
  // Due-in-7 split by entry type
  due_in_7_days_receivable_count: number
  due_in_7_days_receivable_total: number
  due_in_7_days_payable_count: number
  due_in_7_days_payable_total: number
  open_count:          number
  open_total:          number
}

export interface PartyWithStats extends Party, PartyStats {}

// ─── WhatsApp Message History ──────────────────────────────────────────────

export type WhatsAppMessageType = 'charge' | 'duedate_alert'
export type WhatsAppMessageStatus = 'sent' | 'failed'

export interface WhatsAppMessage {
  id: string
  party_id: string
  entry_id: string
  unit_id: string
  type: WhatsAppMessageType
  message_content: string
  phone: string
  sent_at: string
  status: WhatsAppMessageStatus
  error_message: string | null
  created_at: string
}

export interface LogWhatsAppMessageInput {
  party_id: string
  entry_id: string
  unit_id: string
  type: WhatsAppMessageType
  message_content: string
  phone: string
  status?: WhatsAppMessageStatus
  error_message?: string | null
}

export interface CreateEntryInput {
  unit_id:                          string
  parent_id?:                       string
  recurrence_id?:                   string
  recurrence_template_id?:          string | null
  entry_type:                       EntryType
  description:                      string
  amount:                           number
  due_date:                         string
  /** Optional: original due date when creating an entry that was postponed */
  original_due_date?:               string
  party_id?:                        string
  party_name_snapshot?:             string
  party_whatsapp_snapshot?:         string
  party_pix_key_snapshot?:          string
  payment_method_snapshot?:         PaymentMethod
  payment_method_details_snapshot?: Record<string, unknown>
  notes?:                           string
  notify_on_due?:                   boolean
  notify_time?:                     string
  notify_offset_minutes?:           number
}

export type UpdateEntryInput = Partial<Omit<CreateEntryInput, 'unit_id' | 'entry_type'>>

/**
 * Find an existing party in the unit by name (case-insensitive) or create one.
 * Always keeps `whatsapp` up to date with the latest value provided.
 */
export async function upsertParty(
  unitId: string,
  name: string,
  whatsapp?: string,
): Promise<string> {
  const trimmedName = name.trim()

  const { data: existing } = await supabase
    .from('parties')
    .select('id')
    .eq('unit_id', unitId)
    .ilike('name', trimmedName)
    .maybeSingle()

  if (existing) {
    // Refresh whatsapp whenever a new value is provided
    if (whatsapp !== undefined) {
      await supabase
        .from('parties')
        .update({ whatsapp: whatsapp.trim() || null })
        .eq('id', existing.id)
    }
    return existing.id
  }

  const { data: created, error } = await supabase
    .from('parties')
    .insert({ unit_id: unitId, name: trimmedName, whatsapp: whatsapp?.trim() || null })
    .select('id')
    .single()
  if (error) throw error
  return (created as { id: string }).id
}

// ─── Party CRUD ──────────────────────────────────────────────────────────────

export async function listParties(unitId: string, includeInactive = false): Promise<Party[]> {
  let query = supabase
    .from('parties')
    .select('*')
    .eq('unit_id', unitId)
    .order('name', { ascending: true })
  
  // By default, only return active parties
  if (!includeInactive) {
    query = query.eq('is_active', true)
  }
  
  const { data, error } = await query
  if (error) throw error
  return (data as Party[]) || []
}

export async function getPartyById(id: string): Promise<Party | null> {
  const { data, error } = await supabase
    .from('parties')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data as Party | null
}

export async function createParty(
  unitId: string,
  input: PartyInput,
  createdBy: string,
): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .insert({
      unit_id:    unitId,
      name:       input.name.trim(),
      whatsapp:   input.whatsapp?.trim() || null,
      pix_key:    input.pix_key?.trim()  || null,
      notes:      input.notes?.trim()    || null,
      created_by: createdBy,
    })
    .select()
    .single()
  if (error) throw error
  return data as Party
}

export async function updateParty(id: string, input: PartyInput): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update({
      name:     input.name.trim(),
      whatsapp: input.whatsapp?.trim() || null,
      pix_key:  input.pix_key?.trim()  || null,
      notes:    input.notes?.trim()    || null,
    })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Party
}

export async function deleteParty(id: string): Promise<void> {
  const { error } = await supabase
    .from('parties')
    .delete()
    .eq('id', id)
  if (error) throw error
}

/**
 * Inactivate multiple parties (soft-delete).
 * Before inactivating, checks if each party has open entries.
 * Returns detailed result for each party attempted.
 */
export async function inactivateParties(
  unitId: string,
  partyIds: string[],
): Promise<InactivatePartyResult[]> {
  if (partyIds.length === 0) return []

  const results: InactivatePartyResult[] = []

  // Fetch all parties being inactivated
  const { data: parties, error: partiesError } = await supabase
    .from('parties')
    .select('id, name, is_active')
    .in('id', partyIds)
  if (partiesError) throw partiesError

  // Fetch all entries for these parties
  const { data: entries, error: entriesError } = await supabase
    .from('financial_entries')
    .select('id, party_id, entry_type, amount, status')
    .eq('unit_id', unitId)
    .in('party_id', partyIds)
  if (entriesError) throw entriesError

  // Group entries by party (we only selected a subset of fields, use Partial)
  const entriesByParty = new Map<string, Partial<FinancialEntry>[]>()
  for (const entry of entries || []) {
    if (!entry.party_id) continue
    if (!entriesByParty.has(entry.party_id)) {
      entriesByParty.set(entry.party_id, [])
    }
    entriesByParty.get(entry.party_id)!.push(entry)
  }

  // Process each party
  for (const party of parties || []) {
    const partyEntries = entriesByParty.get(party.id) || []
    const openEntries = partyEntries.filter(e => e.status === 'open')
    
    const openReceivable = openEntries.filter(e => e.entry_type === 'receivable')
    const openPayable = openEntries.filter(e => e.entry_type === 'payable')

    const result: InactivatePartyResult = {
      success: false,
      partyId: party.id,
      partyName: party.name,
      wasInactivated: false,
      hasOpenEntries: openEntries.length > 0,
      openReceivableCount: openReceivable.length,
      openReceivableTotal: openReceivable.reduce((s, e) => s + (e.amount ?? 0), 0),
      openPayableCount: openPayable.length,
      openPayableTotal: openPayable.reduce((s, e) => s + (e.amount ?? 0), 0),
    }

    // If already inactive, skip
    if (!party.is_active) {
      result.success = true
      result.wasInactivated = false
      results.push(result)
      continue
    }

    // If has open entries, don't inactivate
    if (openEntries.length > 0) {
      result.success = false
      result.errorMessage = `${openEntries.length} open entries prevent inactivation`
      results.push(result)
      continue
    }

    // Inactivate the party
    try {
      const { error } = await supabase
        .from('parties')
        .update({ is_active: false })
        .eq('id', party.id)
      
      if (error) throw error
      
      result.success = true
      result.wasInactivated = true
    } catch (err) {
      result.success = false
      result.errorMessage = err instanceof Error ? err.message : 'Unknown error'
    }

    results.push(result)
  }

  return results
}

/**
 * Smart delete/inactivate: deletes parties without open entries,
 * inactivates parties with open entries.
 * Returns success count + blocked parties with open amounts.
 */
export interface DeleteOrInactivateResult {
  successful: Array<{ id: string; name: string; action: 'deleted' | 'inactivated' }>
  blocked: Array<{ id: string; name: string; reason: string; open_total: number }>
}

export async function deleteOrInactivateParties(
  unitId: string,
  partyIds: string[],
): Promise<DeleteOrInactivateResult> {
  if (partyIds.length === 0) return { successful: [], blocked: [] }

  const successful: DeleteOrInactivateResult['successful'] = []
  const blocked: DeleteOrInactivateResult['blocked'] = []

  // Fetch all parties being deleted
  const { data: parties, error: partiesError } = await supabase
    .from('parties')
    .select('id, name')
    .in('id', partyIds)
  if (partiesError) throw partiesError

  // Fetch all entries for these parties to check open amounts
  const { data: entries, error: entriesError } = await supabase
    .from('financial_entries')
    .select('id, party_id, entry_type, amount, status')
    .eq('unit_id', unitId)
    .in('party_id', partyIds)
  if (entriesError) throw entriesError

  // Group entries by party and compute open totals
  const partyStats = new Map<string, { openCount: number; openTotal: number }>()
  const entriesByParty = new Map<string, Partial<FinancialEntry>[]>()
  
  for (const entry of entries || []) {
    if (!entry.party_id) continue
    
    if (!entriesByParty.has(entry.party_id)) {
      entriesByParty.set(entry.party_id, [])
    }
    entriesByParty.get(entry.party_id)!.push(entry)

    if (entry.status === 'open') {
      if (!partyStats.has(entry.party_id)) {
        partyStats.set(entry.party_id, { openCount: 0, openTotal: 0 })
      }
      const stats = partyStats.get(entry.party_id)!
      stats.openCount += 1
      stats.openTotal += entry.amount
    }
  }

  // Process each party
  for (const party of parties || []) {
    const stats = partyStats.get(party.id) || { openCount: 0, openTotal: 0 }

    if (stats.openTotal > 0) {
      // Has open entries: inactivate instead of delete
      try {
        const { error } = await supabase
          .from('parties')
          .update({ is_active: false })
          .eq('id', party.id)
        
        if (error) throw error
        
        successful.push({
          id: party.id,
          name: party.name,
          action: 'inactivated',
        })
      } catch (err) {
        blocked.push({
          id: party.id,
          name: party.name,
          reason: 'Erro ao inativar',
          open_total: stats.openTotal,
        })
      }
    } else {
      // No open entries: delete
      try {
        const { error } = await supabase
          .from('parties')
          .delete()
          .eq('id', party.id)
        
        if (error) throw error
        
        successful.push({
          id: party.id,
          name: party.name,
          action: 'deleted',
        })
      } catch (err) {
        blocked.push({
          id: party.id,
          name: party.name,
          reason: 'Erro ao deletar',
          open_total: 0,
        })
      }
    }
  }

  return { successful, blocked }
}

/**
 * Reactivate a party (restore from soft-delete).
 */
export async function reactivateParty(id: string): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update({ is_active: true })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Party
}

/**
 * Merge sourcePartyId into targetPartyId:
 * 1. Reassigns all financial_entries from source to target (updates party_id and party_name_snapshot).
 * 2. Deletes the source party.
 */
export async function mergeParty(sourcePartyId: string, targetParty: Party, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  
  // Call the backend RPC which handles all logic atomically:
  // - Locks both parties
  // - Reassigns all entries from source to target
  // - Updates snapshots with target party data
  // - Deletes source party
  const { error } = await supabase.rpc('merge_party', {
    p_source_id: sourcePartyId,
    p_target_id: targetParty.id
  })
  
  if (error) throw error
}

export async function listEntriesForParty(
  unitId: string,
  partyId: string,
): Promise<FinancialEntry[]> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('*')
    .eq('unit_id', unitId)
    .eq('party_id', partyId)
    .order('due_date', { ascending: false })
  if (error) throw error
  return (data as FinancialEntry[]) || []
}

// ─── Party stats ─────────────────────────────────────────────────────────────

function computePartyStats(entries: FinancialEntry[]): PartyStats {
  const today              = new Date().toISOString().split('T')[0]
  const paidEntries        = entries.filter(e => e.status === 'paid')
  const openEntries        = entries.filter(e => e.status === 'open')
  const overdueEntries     = openEntries.filter(e => e.due_date < today)
  
  // Calculate date 7 days from now
  const sevenDaysLater = new Date()
  sevenDaysLater.setDate(sevenDaysLater.getDate() + 7)
  const sevenDaysLaterStr = sevenDaysLater.toISOString().split('T')[0]
  
  // Due in next 7 days: today <= due_date <= 7 days from now
  const dueIn7DaysEntries = openEntries.filter(e => e.due_date >= today && e.due_date <= sevenDaysLaterStr)

  // Due-in-7 split by entry type
  const dueIn7DaysReceivable = dueIn7DaysEntries.filter(e => e.entry_type === 'receivable')
  const dueIn7DaysPayable    = dueIn7DaysEntries.filter(e => e.entry_type === 'payable')

  // Overdue split by entry type
  const overdueReceivable = overdueEntries.filter(e => e.entry_type === 'receivable')
  const overduePayable    = overdueEntries.filter(e => e.entry_type === 'payable')

  // Totals by entry type (exclude canceled)
  const receivableEntries = entries.filter(e => e.entry_type === 'receivable' && e.status !== 'canceled')
  const payableEntries    = entries.filter(e => e.entry_type === 'payable' && e.status !== 'canceled')

  // score_pct is computed client-side for display purposes (letter grade comes from DB)
  let score_pct: number | null = null
  if (paidEntries.length > 0) {
    const onTime = paidEntries.filter(e => !!e.paid_at && e.paid_at.split('T')[0] <= e.due_date)
    score_pct    = Math.round((onTime.length / paidEntries.length) * 100)
  }

  const paidDates = paidEntries.filter(e => e.paid_at).map(e => e.paid_at!).sort()

  return {
    score_pct,
    first_paid_at:       paidDates[0]?.split('T')[0]                           ?? null,
    last_paid_at:        paidDates[paidDates.length - 1]?.split('T')[0]        ?? null,
    overdue_count:       overdueEntries.length,
    overdue_total:       overdueEntries.reduce((s, e) => s + e.amount, 0),
    overdue_receivable_count: overdueReceivable.length,
    overdue_receivable_total: overdueReceivable.reduce((s, e) => s + e.amount, 0),
    overdue_payable_count:    overduePayable.length,
    overdue_payable_total:    overduePayable.reduce((s, e) => s + e.amount, 0),
    receivable_count: receivableEntries.length,
    receivable_total: receivableEntries.reduce((s, e) => s + e.amount, 0),
    payable_count:    payableEntries.length,
    payable_total:    payableEntries.reduce((s, e) => s + e.amount, 0),
    due_in_7_days_count: dueIn7DaysEntries.length,
    due_in_7_days_total: dueIn7DaysEntries.reduce((s, e) => s + e.amount, 0),
    due_in_7_days_receivable_count: dueIn7DaysReceivable.length,
    due_in_7_days_receivable_total: dueIn7DaysReceivable.reduce((s, e) => s + e.amount, 0),
    due_in_7_days_payable_count: dueIn7DaysPayable.length,
    due_in_7_days_payable_total: dueIn7DaysPayable.reduce((s, e) => s + e.amount, 0),
    open_count:          openEntries.length,
    open_total:          openEntries.reduce((s, e) => s + e.amount, 0),
  }
}

export async function listPartiesWithStats(unitId: string, includeInactive = false): Promise<PartyWithStats[]> {
  const [parties, entries] = await Promise.all([
    listParties(unitId, includeInactive),
    listEntries(unitId),
  ])

  const byParty = new Map<string, FinancialEntry[]>()
  for (const entry of entries) {
    if (!entry.party_id) continue
    if (!byParty.has(entry.party_id)) byParty.set(entry.party_id, [])
    byParty.get(entry.party_id)!.push(entry)
  }

  return parties.map(party => ({
    ...party,
    ...computePartyStats(byParty.get(party.id) ?? []),
  }))
}

export interface ListEntriesFilters {
  entry_type?:  EntryType
  status?:      EntryStatus[]
  dueDateFrom?: string  // 'YYYY-MM-DD'
  dueDateTo?:   string  // 'YYYY-MM-DD'
}

/** Returns true when an entry is past due but not yet paid or canceled. */
export function isOverdue(entry: FinancialEntry): boolean {
  return entry.status === 'open' && entry.due_date < new Date().toISOString().split('T')[0]
}

export async function listEntries(
  unitId: string,
  filters?: ListEntriesFilters,
): Promise<FinancialEntry[]> {
  let query = supabase
    .from('financial_entries')
    .select('*')
    .eq('unit_id', unitId)
    .order('due_date', { ascending: true })

  if (filters?.entry_type)  query = query.eq('entry_type', filters.entry_type)
  if (filters?.status)      query = query.in('status', filters.status)
  if (filters?.dueDateFrom) query = query.gte('due_date', filters.dueDateFrom)
  if (filters?.dueDateTo)   query = query.lte('due_date', filters.dueDateTo)

  const { data, error } = await query
  if (error) throw error
  
  const entries = (data as FinancialEntry[]) || []
  
  // Get unique party IDs and fetch current pix_key from parties table
  const partyIds = [...new Set(entries.map(e => e.party_id).filter(Boolean))]
  if (partyIds.length > 0) {
    try {
      const { data: parties, error: partiesError } = await supabase
        .from('parties')
        .select('id, pix_key')
        .in('id', partyIds)
      
      if (!partiesError && parties) {
        const pixKeyMap = new Map(parties.map((p: any) => [p.id, p.pix_key]))
        // Update party_pix_key_snapshot with current pix_key from parties table
        entries.forEach(entry => {
          if (entry.party_id && pixKeyMap.has(entry.party_id)) {
            const val = pixKeyMap.get(entry.party_id)
            entry.party_pix_key_snapshot = val == null ? null : String(val)
          }
        })
      }
    } catch (err) {
      console.warn('[listEntries] Error fetching current pix keys:', err)
    }
  }
  
  return entries
}

export async function createEntry(input: CreateEntryInput, userRole?: Role): Promise<FinancialEntry> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const { data, error } = await supabase
    .from('financial_entries')
    .insert(input)
    .select()
    .single()
  if (error) throw error
  return data as FinancialEntry
}

export async function updateEntry(id: string, input: UpdateEntryInput, userRole?: Role): Promise<FinancialEntry> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const { data, error } = await supabase
    .from('financial_entries')
    .update(input)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as FinancialEntry
}

export async function deleteEntry(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canDeleteEntries')
  }
  const { error } = await supabase
    .from('financial_entries')
    .delete()
    .eq('id', id)
  if (error) throw error
}

export async function markAsPaid(id: string, paidAt?: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const paidAtValue = paidAt
    ? (() => { const [y, m, d] = paidAt.split('-').map(Number); return new Date(y, m - 1, d).toISOString() })()
    : new Date().toISOString()
  const { error } = await supabase
    .from('financial_entries')
    .update({ status: 'paid', paid_at: paidAtValue })
    .eq('id', id)
  if (error) throw error
}

export async function undoPayment(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  
  // Call the backend RPC which handles all logic atomically:
  // - Checks for remainder children
  // - Restores original amount if needed
  // - Deletes children
  // - Updates status
  const { error } = await supabase.rpc('undo_payment', {
    p_entry_id: id
  })
  
  if (error) throw error
}

export async function partialMarkAsPaid(
  entry: FinancialEntry,
  partialAmount: number,
  paidAt?: string,
  userRole?: Role
): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const remainder = entry.amount - partialAmount
  const paidAtValue = paidAt
    ? (() => { const [y, m, d] = paidAt.split('-').map(Number); return new Date(y, m - 1, d).toISOString() })()
    : new Date().toISOString()

  // If this entry is the origin of a recurrence template, ensure the template
  // stores the original amount before we reduce the entry's amount. The recurrence
  // should generate future occurrences with the ORIGINAL FULL AMOUNT, not affected
  // by partial payments. Set origin_amount to the full value BEFORE reducing entry.amount.
  try {
    const { error: recurrenceError } = await supabase
      .from('financial_entry_recurrences')
      .update({ origin_amount: entry.amount })
      .eq('origin_entry_id', entry.id)
    if (recurrenceError) {
      // not fatal; log and continue
      console.warn('[partialMarkAsPaid] failed to set origin_amount on recurrence', recurrenceError)
    }
  } catch (err) {
    console.warn('[partialMarkAsPaid] error while setting origin_amount on recurrence', err)
  }

  const { error: updateError } = await supabase
    .from('financial_entries')
    .update({ amount: partialAmount, status: 'paid', paid_at: paidAtValue })
    .eq('id', entry.id)
  if (updateError) throw updateError

  const { data: createdChild, error: createError } = await supabase
    .from('financial_entries')
    .insert({
      unit_id:                 entry.unit_id,
      parent_id:               entry.id,
      entry_type:              entry.entry_type,
      description:             entry.description,
      amount:                  remainder,
      due_date:                entry.due_date,
      status:                  'open',
      party_id:                entry.party_id,
      party_name_snapshot:     entry.party_name_snapshot,
      party_whatsapp_snapshot: entry.party_whatsapp_snapshot,
      payment_method_snapshot: entry.payment_method_snapshot,
      notes:                   entry.notes,
      // Preserve notification settings on the residual open parcel so alerts behave the same
      notify_on_due:           entry.notify_on_due ?? false,
      notify_time:             entry.notify_time ?? null,
      notify_offset_minutes:   entry.notify_offset_minutes ?? null,
    })
    .select()
    .single()
  if (createError) throw createError
}

export async function cancelEntry(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canDeleteEntries')
  }
  const { error } = await supabase
    .from('financial_entries')
    .update({ status: 'canceled' })
    .eq('id', id)
  if (error) throw error
}

export async function replicateEntry(entry: FinancialEntry): Promise<FinancialEntry> {
  // Creates a new independent entry with the same data as the source
  // Does not copy id, parent_id (clean start), created_at, updated_at
  const { data, error } = await supabase
    .from('financial_entries')
    .insert({
      unit_id:                 entry.unit_id,
      entry_type:              entry.entry_type,
      description:             entry.description,
      amount:                  entry.amount,
      due_date:                entry.due_date,
      status:                  'open',
      party_id:                entry.party_id,
      party_name_snapshot:     entry.party_name_snapshot,
      party_whatsapp_snapshot: entry.party_whatsapp_snapshot,
      payment_method_snapshot: entry.payment_method_snapshot,
      payment_method_details_snapshot: entry.payment_method_details_snapshot,
      notes:                   entry.notes,
    })
    .select()
    .single()
  if (error) throw error
  return data as FinancialEntry
}

export async function replicateEntryBatch(
  entry: FinancialEntry,
  occurrences: Array<{ due_date: string; description: string }>,
): Promise<void> {
  if (occurrences.length === 0) return

  // Reuse existing recurrence_id or generate a new one for this chain
  const recurrenceId = entry.recurrence_id ?? crypto.randomUUID()

  // Stamp the source entry if it doesn't already belong to a chain
  if (!entry.recurrence_id) {
    await supabase
      .from('financial_entries')
      .update({ recurrence_id: recurrenceId })
      .eq('id', entry.id)
  }

  const rows = occurrences.map(occ => ({
    unit_id:                         entry.unit_id,
    recurrence_id:                   recurrenceId,
    entry_type:                      entry.entry_type,
    description:                     occ.description,
    amount:                          entry.amount,
    due_date:                        occ.due_date,
    status:                          'open' as const,
    party_id:                        entry.party_id,
    party_name_snapshot:             entry.party_name_snapshot,
    party_whatsapp_snapshot:         entry.party_whatsapp_snapshot,
    payment_method_snapshot:         entry.payment_method_snapshot,
    payment_method_details_snapshot: entry.payment_method_details_snapshot,
    notes:                           entry.notes,
  }))
  const { error } = await supabase.from('financial_entries').insert(rows)
  if (error) throw error
}

/** Fetch a single entry by id (used to inspect recurrence_id before delete). */
export async function fetchEntry(id: string): Promise<FinancialEntry | null> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  const entry = data as FinancialEntry | null
  if (!entry) return null

  // If the entry references a party, fetch the current pix_key from parties
  try {
    if (entry.party_id) {
      const { data: partyData, error: partyError } = await supabase
        .from('parties')
        .select('id, pix_key')
        .eq('id', entry.party_id)
        .maybeSingle()
      if (!partyError && partyData) {
        entry.party_pix_key_snapshot = partyData.pix_key ?? entry.party_pix_key_snapshot
      }
    }
  } catch (err) {
    console.warn('[fetchEntry] failed to fetch party pix_key:', err)
  }

  return entry
}

/**
 * Count future open entries that share the same recurrence_id as `entry`
 * and have a due_date strictly after `entry.due_date`.
 */
export async function countFutureRecurrences(entry: FinancialEntry): Promise<number> {
  if (!entry.recurrence_id) return 0
  const { count, error } = await supabase
    .from('financial_entries')
    .select('id', { count: 'exact', head: true })
    .eq('recurrence_id', entry.recurrence_id)
    .neq('id', entry.id)
    .eq('status', 'open')
    .gt('due_date', entry.due_date)
  if (error) throw error
  return count ?? 0
}

/**
 * Fetch the next entry in the same recurrence chain (due_date strictly after `afterDate`).
 */
export async function fetchNextRecurrence(recurrenceId: string, afterDate: string): Promise<FinancialEntry | null> {
  if (!recurrenceId) return null
  const { data, error } = await supabase
    .from('financial_entries')
    .select('*')
    .eq('recurrence_id', recurrenceId)
    .gt('due_date', afterDate)
    .order('due_date', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as FinancialEntry) || null
}

/**
 * Apply `input` (everything except due_date) to all future open entries
 * in the same recurrence chain.
 */
export async function updateFutureRecurrences(
  entry: FinancialEntry,
  input: Omit<UpdateEntryInput, 'due_date'>,
): Promise<void> {
  if (!entry.recurrence_id) return
  if (Object.keys(input).length === 0) return
  const { error } = await supabase
    .from('financial_entries')
    .update(input)
    .eq('recurrence_id', entry.recurrence_id)
    .neq('id', entry.id)
    .eq('status', 'open')
    .gt('due_date', entry.due_date)
  if (error) throw error
}

/**
 * Delete all future open entries in the same recurrence chain.
 */
export async function deleteFutureRecurrences(entry: FinancialEntry): Promise<void> {
  if (!entry.recurrence_id) return
  const { error } = await supabase
    .from('financial_entries')
    .delete()
    .eq('recurrence_id', entry.recurrence_id)
    .neq('id', entry.id)
    .eq('status', 'open')
    .gt('due_date', entry.due_date)
  if (error) throw error
}

// ─── Recurrence Templates ─────────────────────────────────────────────────────

/** Create a new recurrence template and return it. */
export async function createRecurrenceTemplate(
  input: RecurrenceTemplateInput,
): Promise<RecurrenceTemplate> {
  // Determine origin_amount: prefer explicit input; otherwise attempt to
  // reconstruct the original full amount by summing the origin entry plus
  // any immediate child parcels (created by partial payments). This lets
  // us recover the original value even when a partial payment already
  // modified the origin row.
  let originAmount = input.origin_amount ?? 0
  try {
    if (!originAmount || originAmount === 0) {
      const { data: related, error: relErr } = await supabase
        .from('financial_entries')
        .select('amount')
        .or(`id.eq.${input.origin_entry_id},parent_id.eq.${input.origin_entry_id}`)
      if (relErr) {
        console.warn('[createRecurrenceTemplate] failed to fetch related entries for origin_amount', relErr)
      } else if (related && related.length > 0) {
        originAmount = related.reduce((s: number, r: any) => s + Number(r.amount || 0), 0)
      }
    }
  } catch (err) {
    console.warn('[createRecurrenceTemplate] error while computing origin_amount', err)
  }

  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .insert({
      unit_id:         input.unit_id,
      origin_entry_id: input.origin_entry_id,
      periodicity:     input.periodicity,
      rules_json:      input.rules_json ?? {},
      start_date:      input.start_date,
      origin_amount:   originAmount ?? 0,
    })
    .select()
    .single()
  if (error) throw error
  return data as RecurrenceTemplate
}

/** Fetch the recurrence template linked to a specific entry (by origin_entry_id). */
export async function getRecurrenceTemplateByEntry(
  originEntryId: string,
): Promise<RecurrenceTemplate | null> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('origin_entry_id', originEntryId)
    .maybeSingle()
  if (error) throw error
  return (data as RecurrenceTemplate) || null
}

/** List all active recurrence templates for a unit. */
export async function listRecurrenceTemplates(
  unitId: string,
): Promise<RecurrenceTemplate[]> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('unit_id', unitId)
    .eq('status', 'active')
    .order('start_date', { ascending: true })
  if (error) throw error
  return (data as RecurrenceTemplate[]) || []
}

/** Update the rules or periodicity of an existing template. */
export async function updateRecurrenceTemplate(
  id: string,
  patch: Partial<Pick<RecurrenceTemplate, 'periodicity' | 'rules_json' | 'end_date'>>,
): Promise<RecurrenceTemplate> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as RecurrenceTemplate
}

/** Terminate a recurrence template — sets status='terminated' and end_date=today. */
export async function terminateRecurrenceTemplate(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canTerminateRecurrences')
  }
  const today = new Date().toISOString().split('T')[0]
  const { error } = await supabase
    .from('financial_entry_recurrences')
    .update({ status: 'terminated', end_date: today })
    .eq('id', id)
  if (error) throw error
}

/**
 * Terminates a recurrence template by hard-deleting it.
 *
 * Terminates a recurrence template.
 *
 * - If `endDate` is in the FUTURE: soft-terminate (status='terminated', end_date set).
 *   The template must remain alive so that virtual entries between now and end_date
 *   can still be generated and materialised.  Expired soft-terminated rows are
 *   automatically removed by cleanupExpiredRecurrences().
 *
 * - Otherwise (no endDate, or endDate is today / in the past): hard-delete.
 *   The FK ON DELETE SET NULL clears recurrence_template_id on every linked entry.
 */
export async function smartTerminateRecurrenceTemplate(
  templateId:    string,
  originEntryId: string,
  endDate?:      string,
  userRole?:     Role,
): Promise<{ wasDeleted: boolean }> {
  if (userRole) {
    await validateCapability(userRole, 'canTerminateRecurrences')
  }
  const today = new Date().toISOString().split('T')[0]
  const hasFutureEndDate = endDate != null && endDate > today

  if (hasFutureEndDate) {
    // Soft-terminate: keep template so remaining virtual entries are generated until end_date
    const { error } = await supabase
      .from('financial_entry_recurrences')
      .update({ status: 'terminated', end_date: endDate })
      .eq('id', templateId)
    if (error) throw error
    return { wasDeleted: false }
  } else {
    // Hard-delete: FK ON DELETE SET NULL unlinks all materialized entries automatically
    const { error } = await supabase
      .from('financial_entry_recurrences')
      .delete()
      .eq('id', templateId)
    if (error) throw error
    return { wasDeleted: true }
  }
}

/**
 * Count how many financial entries are linked to a recurrence template
 * (other than the origin entry itself).
 */
export async function countEntriesWithTemplate(templateId: string, originEntryId: string): Promise<number> {
  const { count, error } = await supabase
    .from('financial_entries')
    .select('id', { count: 'exact', head: true })
    .eq('recurrence_template_id', templateId)
    .neq('id', originEntryId)
  if (error) throw error
  return count ?? 0
}

/**
 * For installment recurrences: deletes the template when every installment slot
 * has been materialized (i.e. a non-canceled entry exists for each expected slot).
 *
 * The FK ON DELETE SET NULL on financial_entries.recurrence_template_id
 * automatically unlinks every materialized entry when the template is deleted.
 *
 * Safe to call for non-installment templates — returns immediately.
 */
export async function maybeCleanupInstallmentTemplate(
  template: RecurrenceTemplate,
): Promise<void> {
  if (template.periodicity !== 'installment') return

  const rules   = template.rules_json
  const total   = rules?.installment_total         ?? 0
  const start   = rules?.installment_current_start ?? 1
  const expected = total - start + 1
  if (expected <= 0) return

  // Count non-canceled entries covering this template, including the origin entry
  // (which may or may not have recurrence_template_id set at the time of the check).
  const { count, error: countErr } = await supabase
    .from('financial_entries')
    .select('id', { count: 'exact', head: true })
    .or(`recurrence_template_id.eq.${template.id},id.eq.${template.origin_entry_id}`)
    .neq('status', 'canceled')
  if (countErr) throw countErr

  if ((count ?? 0) >= expected) {
    const { error: delErr } = await supabase
      .from('financial_entry_recurrences')
      .delete()
      .eq('id', template.id)
    if (delErr) throw delErr
    // FK ON DELETE SET NULL handles unlinking all entries automatically
  }
}

/**
 * Rolls the template's origin_entry_id to the most recently materialised entry,
 * keeping the template always anchored to current data.
 *
 * For non-installment recurrences also clears recurrence_template_id from entries
 * whose occurrence date is older than 35 days — safely outside the dedup window
 * used by get_virtual_entries — so that old paid entries are not permanently bound
 * to the template.
 *
 * Installment series are intentionally left intact: all parcel links must remain
 * for maybeCleanupInstallmentTemplate to count them correctly.
 *
 * Safe to call even if the template was already deleted (update is a no-op).
 */
export async function rollTemplateOrigin(
  template:         RecurrenceTemplate,
  newOriginEntryId: string,
): Promise<void> {
  // Update origin — template may have been deleted by maybeCleanupInstallmentTemplate;
  // in that case this update affects 0 rows without error.
  const { error: updateErr } = await supabase
    .from('financial_entry_recurrences')
    .update({ origin_entry_id: newOriginEntryId })
    .eq('id', template.id)
  if (updateErr) throw updateErr

  // For installment series: keep all links so completion counting stays accurate
  if (template.periodicity === 'installment') return

  // Clear entries outside the 35-day dedup window
  // Uses COALESCE(original_due_date, due_date) logic via .or()
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 35)
  const cutoffDate = cutoff.toISOString().split('T')[0]

  const { error: clearErr } = await supabase
    .from('financial_entries')
    .update({ recurrence_template_id: null })
    .eq('recurrence_template_id', template.id)
    .neq('id', newOriginEntryId)
    .or(
      `and(original_due_date.not.is.null,original_due_date.lt.${cutoffDate}),` +
      `and(original_due_date.is.null,due_date.lt.${cutoffDate})`,
    )
  if (clearErr) throw clearErr
}

/**
 * Before deleting an entry that is the template's current origin_entry_id,
 * hands the origin role to the most recent OTHER linked entry so that
 * the template is not cascade-deleted unexpectedly.
 *
 * Returns true if the baton was passed successfully.
 * Returns false if no candidate exists — caller should let the cascade proceed.
 */
export async function handoverTemplateOrigin(
  templateId:       string,
  currentOriginId:  string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('id')
    .eq('recurrence_template_id', templateId)
    .neq('id', currentOriginId)
    .order('due_date', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  if (!data) return false

  const { error: updateErr } = await supabase
    .from('financial_entry_recurrences')
    .update({ origin_entry_id: data.id })
    .eq('id', templateId)
  if (updateErr) throw updateErr
  return true
}

/**
 * Deletes soft-terminated recurrence templates whose end_date has already passed.
 * These templates can no longer generate virtual entries (get_virtual_entries
 * filters WHERE t.status = 'active') and serve no further purpose.
 *
 * Called lazily from fetchVirtualEntries so the cleanup is transparent to the user.
 * Wrapped in try-catch at call-site — failure is non-critical.
 */
export async function cleanupExpiredRecurrences(unitId: string): Promise<void> {
  const today = new Date().toISOString().split('T')[0]
  const { error } = await supabase
    .from('financial_entry_recurrences')
    .delete()
    .eq('unit_id', unitId)
    .eq('status', 'terminated')
    .lt('end_date', today)
  if (error) throw error
}

/**
 * Find the immediately preceding terminated template for a given template.
 * Looks for a terminated template whose end_date equals the day before 
 * the current template's start_date.
 *
 * This is used when a user deletes an entry from a template created by
 * "esta e futuras" — to check if we should re-activate the previous template.
 */
export async function findPreviousTerminatedTemplate(
  unitId: string,
  currentTemplateStartDate: string,
): Promise<RecurrenceTemplate | null> {
  // Calculate the day before start_date
  const startDate = new Date(currentTemplateStartDate + 'T12:00:00')
  startDate.setDate(startDate.getDate() - 1)
  const dayBefore = startDate.toISOString().split('T')[0]

  // The previous template may have status 'active' (just capped with end_date) or 'terminated'.
  // When "esta e futuras" is applied, the old template keeps status='active' but gets end_date set.
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('unit_id', unitId)
    .eq('end_date', dayBefore)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as RecurrenceTemplate) || null
}

/**
 * Reactivate a terminated template by removing its end_date.
 * Used when a user cancels a "esta e futuras" operation by deleting the
 * entry created during that split.
 */
export async function reactivateTemplate(templateId: string): Promise<void> {
  const { error } = await supabase
    .from('financial_entry_recurrences')
    .update({ status: 'active', end_date: null })
    .eq('id', templateId)
  if (error) throw error
}

/** Get a recurrence template by its ID. */
export async function getRecurrenceTemplate(id: string): Promise<RecurrenceTemplate | null> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return (data as RecurrenceTemplate) || null
}

// ─── Virtual entry computation ────────────────────────────────────────────

// ── Internal date helpers (mirror the UI helpers so logic lives in one place) ──

function _addMonths(dateStr: string, n: number, targetDay?: number): string {
  const [y, m, d]   = dateStr.split('-').map(Number)
  const targetMonth = (m - 1) + n
  const targetYear  = y + Math.floor(targetMonth / 12)
  const finalMonth  = ((targetMonth % 12) + 12) % 12
  const maxDay      = new Date(targetYear, finalMonth + 1, 0).getDate()
  // Use targetDay if provided, otherwise use original day
  const dayToUse    = targetDay ?? d
  return `${targetYear}-${String(finalMonth + 1).padStart(2, '0')}-${String(Math.min(dayToUse, maxDay)).padStart(2, '0')}`
}

function _addYears(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const maxDay    = new Date(y + n, m, 0).getDate()
  return `${y + n}-${String(m).padStart(2, '0')}-${String(Math.min(d, maxDay)).padStart(2, '0')}`
}

function _addWeeks(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  date.setDate(date.getDate() + n * 7)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function _addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  date.setDate(date.getDate() + n)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function _toDateStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function _snapToWeekday(dateStr: string, dow: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  const diff      = (dow - date.getDay() + 7) % 7
  if (diff > 0) date.setDate(date.getDate() + diff)
  return _toDateStr(date)
}

function _adjustWeekend(dateStr: string, mode: 'next' | 'prev'): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  const dow       = date.getDay()
  if (dow === 0) date.setDate(date.getDate() + (mode === 'next' ? 1  : -2))
  if (dow === 6) date.setDate(date.getDate() + (mode === 'next' ? 2  : -1))
  return _toDateStr(date)
}

function _applyRules(dateStr: string, rules: RecurrenceRules): string {
  let result = dateStr

  // Check if day-of-week is allowed (for daily periodicity with weekday filter)
  if (rules.daily_weekdays && rules.daily_weekdays.length > 0) {
    const dow = new Date(dateStr + 'T00:00:00').getDay()
    if (!rules.daily_weekdays.includes(dow)) {
      return '' // Signal to skip this date
    }
  }

  if (rules.weekday_lock && rules.weekday_lock_day != null)
    result = _snapToWeekday(result, rules.weekday_lock_day)
  if (rules.weekend_adjust && rules.weekend_adjust !== 'none')
    result = _adjustWeekend(result, rules.weekend_adjust)
  return result
}

// ── Public service functions ──────────────────────────────────────────────

/**
 * Fetch recurrence templates for a unit that are relevant for virtual entry generation.
 * Includes both 'active' templates and 'terminated' ones (which still need to generate
 * virtual entries up to their end_date).
 */
export async function listActiveRecurrenceTemplates(
  unitId: string,
): Promise<RecurrenceTemplate[]> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('unit_id', unitId)
    .in('status', ['active', 'terminated'])
  if (error) throw error
  return (data as RecurrenceTemplate[]) || []
}

/**
 * Calculate all occurrence dates for a template that fall within [monthStart, monthEnd].
 *
 * - `start_date` is the anchor (first real entry date).
 * - `end_date`   limits the series; NULL = open-ended.
 * - Returns raw date strings 'YYYY-MM-DD' with rules_json applied.
 */
export function generateRecurrenceDates(
  template:   RecurrenceTemplate,
  monthStart: string,   // 'YYYY-MM-DD'  (first day of visible month)
  monthEnd:   string,   // 'YYYY-MM-DD'  (last  day of visible month)
): string[] {
  const { periodicity, rules_json: rules, start_date, end_date } = template
  const seriesEnd = end_date ?? monthEnd   // cap at month when open-ended

  const dates: string[] = []

  if (periodicity === 'daily') {
    // Walk day-by-day starting from the day AFTER start_date until we pass monthEnd or seriesEnd
    // Safety cap: 36500 iterations (~100 years of daily)
    for (let i = 1; i <= 36500; i++) {
      const cursor   = _addDays(start_date, i)
      const adjusted = _applyRules(cursor, rules)
      if (adjusted === '') continue  // Skip if day-of-week filter excluded this date
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
    }

  } else if (periodicity === 'monthly') {
    // Walk month-by-month from start_date until we pass monthEnd or seriesEnd
    let cursor = start_date
    const targetDay = rules.monthly_target_day
    // Safety cap: 1200 iterations (~100 years of monthly)
    for (let i = 0; i < 1200; i++) {
      const adjusted = _applyRules(cursor, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
      cursor = _addMonths(start_date, i + 1, targetDay)
      if (cursor > monthEnd && cursor > seriesEnd) break
    }

  } else if (periodicity === 'weekly') {
    const dow = rules.weekly_dow ?? new Date(
      Number(start_date.slice(0, 4)),
      Number(start_date.slice(5, 7)) - 1,
      Number(start_date.slice(8, 10)),
    ).getDay()
    // Find first occurrence on or after start_date on the right day-of-week
    let cursor = _snapToWeekday(start_date, dow)
    for (let i = 0; i < 5200; i++) {   // 100 years of weekly
      const adjusted = _applyRules(cursor, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
      cursor = _addWeeks(cursor, 1)
    }

  } else if (periodicity === 'yearly') {
    let cursor = start_date
    for (let i = 0; i < 200; i++) {    // 200 years of yearly
      const adjusted = _applyRules(cursor, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
      cursor = _addYears(start_date, i + 1)
    }

  } else if (periodicity === 'installment') {
    const total = rules.installment_total ?? 1
    const targetDay = rules.monthly_target_day
    for (let i = 0; i < total; i++) {
      const raw      = _addMonths(start_date, i, targetDay)
      const adjusted = _applyRules(raw, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
    }
  }

  return dates
}

/**
 * Fetch virtual entries for a unit within a date range.
 * Calls the get_virtual_entries RPC (migration 061) which generates occurrences
 * on demand — no artificial cutoff, calculates until template's end_date or
 * the user explicitly terminates the recurrence.
 *
 * @param unitId   The unit to fetch virtual entries for
 * @param fromDate Start of range (YYYY-MM-DD)
 * @param toDate   End of range (YYYY-MM-DD)
 */
export async function fetchVirtualEntries(
  unitId: string,
  fromDate: string,
  toDate: string,
): Promise<VirtualEntry[]> {
  // Lazy cleanup: remove soft-terminated templates whose end_date has passed
  try { await cleanupExpiredRecurrences(unitId) } catch { /* non-critical */ }

  const { data, error } = await supabase.rpc('get_virtual_entries', {
    p_unit_id:   unitId,
    p_from_date: fromDate,
    p_to_date:   toDate,
  })

  if (error) throw error

  return (data || []).map((row: Record<string, unknown>) => ({
    ...row,
    isVirtual: true,
    _template: {
      id: row.recurrence_template_id,
      unit_id: row.unit_id,
      origin_entry_id: row.id,
      periodicity: 'monthly' as const,
      rules_json: {},
      start_date: row.due_date,
      end_date: null,
      origin_amount: row.amount,
      status: 'active' as const,
      created_by: row.created_by,
      created_at: row.created_at,
      updated_at: row.updated_at,
    },
  } as VirtualEntry))
}

// ─── WhatsApp Message Logging ──────────────────────────────────────────────

/**
 * Log a WhatsApp message sent to a party for a specific financial entry.
 * Useful for tracking charge reminders and duedate alerts and avoiding duplicates.
 */
export async function logWhatsAppMessage(input: LogWhatsAppMessageInput): Promise<WhatsAppMessage> {
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .insert({
      party_id: input.party_id,
      entry_id: input.entry_id,
      unit_id: input.unit_id,
      type: input.type,
      message_content: input.message_content,
      phone: input.phone,
      status: input.status ?? 'sent',
      error_message: input.error_message ?? null,
    })
    .select()
    .single()
  if (error) throw error
  return data as WhatsAppMessage
}

/**
 * Get all WhatsApp messages sent for a specific financial entry.
 */
export async function getMessagesForEntry(entryId: string): Promise<WhatsAppMessage[]> {
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('*')
    .eq('entry_id', entryId)
    .order('sent_at', { ascending: false })
  if (error) throw error
  return (data as WhatsAppMessage[]) || []
}

/**
 * Get all WhatsApp messages sent for a specific party (across all entries).
 */
export async function getMessagesForParty(partyId: string, unitId: string): Promise<WhatsAppMessage[]> {
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('*')
    .eq('party_id', partyId)
    .eq('unit_id', unitId)
    .order('sent_at', { ascending: false })
  if (error) throw error
  return (data as WhatsAppMessage[]) || []
}

/**
 * Get all messages of a specific type for a party today.
 * Useful to check if already sent charge/alert today.
 */
export async function getMessagesForPartyToday(
  partyId: string,
  unitId: string,
  type: WhatsAppMessageType,
): Promise<WhatsAppMessage[]> {
  const today = new Date().toISOString().split('T')[0]
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('*')
    .eq('party_id', partyId)
    .eq('unit_id', unitId)
    .eq('type', type)
    .gte('sent_at', `${today}T00:00:00`)
    .lt('sent_at', `${today}T23:59:59`)
    .order('sent_at', { ascending: false })
  if (error) throw error
  return (data as WhatsAppMessage[]) || []
}

/**
 * Count how many times a message was sent for a specific entry.
 */
export async function countMessagesForEntry(
  entryId: string,
  type?: WhatsAppMessageType,
): Promise<number> {
  let query = supabase
    .from('whatsapp_messages')
    .select('*', { count: 'exact', head: true })
    .eq('entry_id', entryId)
  
  if (type) query = query.eq('type', type)
  
  const { count, error } = await query
  if (error) throw error
  return count ?? 0
}

// ─── Text & PDF Generation for Shareable Accounts ──────────────────────────────

/**
 * Generate plain text content with financial entries for sharing via Web Share API.
 */
export async function generateAccountsText(
  entries: FinancialEntry[],
  locale: string,
  currency: string,
  dateFrom: string,
  dateTo: string,
): Promise<string> {
  try {
    // Format date range
    const [yearFrom, monthFrom, dayFrom] = dateFrom.split('-')
    const [yearTo, monthTo, dayTo] = dateTo.split('-')
    const formattedFrom = new Date(Number(yearFrom), Number(monthFrom) - 1, Number(dayFrom)).toLocaleDateString(locale)
    const formattedTo = new Date(Number(yearTo), Number(monthTo) - 1, Number(dayTo)).toLocaleDateString(locale)

    let text = `CONTAS DE ${formattedFrom} A ${formattedTo}\n`
    text += `Gerado em: ${new Date().toLocaleDateString(locale)} às ${new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}\n`
    text += '\n' + '='.repeat(100) + '\n\n'

    // Buscar chaves PIX do banco para todas as payables
    const partyIds = entries
      .filter(e => e.entry_type === 'payable')
      .map(e => e.party_id)
      .filter(Boolean)
    
    const partyPixMap: Record<string, string> = {}
    if (partyIds.length > 0) {
      const { data: parties } = await supabase
        .from('parties')
        .select('id, pix_key')
        .in('id', [...new Set(partyIds)])
      
      if (parties) {
        for (const p of parties) {
          if (p.pix_key) partyPixMap[p.id] = p.pix_key
        }
      }
    }

    // Group by type
    const receivables = entries.filter(e => e.entry_type === 'receivable')
    const payables = entries.filter(e => e.entry_type === 'payable')

    if (receivables.length > 0) {
      text += 'CONTAS A RECEBER\n' + '-'.repeat(100) + '\n\n'
      for (const entry of receivables) {
        const description = entry.description || '—'
        const person = entry.party_name_snapshot || '—'
        const amount = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(entry.amount)
        const [year, month, day] = entry.due_date.split('-')
        const dueDate = new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(locale)
        const method = entry.payment_method_snapshot?.toUpperCase() || '—'
        
        text += `Descrição: ${description}\n`
        text += `Pessoa: ${person}\n`
        text += `Valor: ${amount}\n`
        text += `Vencimento: ${dueDate}\n`
        text += `Método: ${method}\n`
        text += '\n'
      }
      text += '\n'
    }

    if (payables.length > 0) {
      text += 'CONTAS A PAGAR\n' + '-'.repeat(100) + '\n\n'
      for (const entry of payables) {
        const description = entry.description || '—'
        const person = entry.party_name_snapshot || '—'
        const amount = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(entry.amount)
        const [year, month, day] = entry.due_date.split('-')
        const dueDate = new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(locale)
        
        let pixKey = ''
        if (entry.party_id && partyPixMap[entry.party_id]) {
          pixKey = partyPixMap[entry.party_id]
        } else if (entry.party_pix_key_snapshot) {
          pixKey = entry.party_pix_key_snapshot
        }
        
        text += `Descrição: ${description}\n`
        text += `Pessoa: ${person}\n`
        text += `Valor: ${amount}\n`
        text += `Vencimento: ${dueDate}\n`
        if (pixKey) text += `Chave PIX: ${pixKey}\n`
        text += '\n'
      }
    }

    return text
  } catch (err) {
    console.error('[generateAccountsText] Error generating text:', err)
    throw err
  }
}

/**
 * Generate a PDF document with financial entries formatted for sharing.
 * Includes values and Pix keys that can be copied directly from the PDF.
 * Downloads the PDF directly to the user's device.
 */
export async function generateAccountsPDF(
  entries: FinancialEntry[],
  locale: string,
  currency: string,
  dateFrom: string,
  dateTo: string,
): Promise<void> {
  try {
    console.log('[generateAccountsPDF] Iniciando import de pdfmake...')
    // Dynamic import to avoid bundling pdfmake if not used
    const pdfMakeModule = await import('pdfmake/build/pdfmake') as any
    const pdfMake = pdfMakeModule.default || pdfMakeModule
    
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts') as any
    const pdfFonts = pdfFontsModule.default || pdfFontsModule
    
    console.log('[generateAccountsPDF] Configurando VFS fonts...')
    // Set VFS fonts
    if (pdfFonts?.pdfMake?.vfs) {
      ;(pdfMake as any).vfs = pdfFonts.pdfMake.vfs
    }

    // Format date range for title
    const [yearFrom, monthFrom, dayFrom] = dateFrom.split('-')
    const [yearTo, monthTo, dayTo] = dateTo.split('-')
    const formattedFrom = new Date(Number(yearFrom), Number(monthFrom) - 1, Number(dayFrom)).toLocaleDateString(locale)
    const formattedTo = new Date(Number(yearTo), Number(monthTo) - 1, Number(dayTo)).toLocaleDateString(locale)
    const title = `Contas de ${formattedFrom} a ${formattedTo}`

    const docContent: any[] = [
      {
        text: title,
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 10],
        alignment: 'center',
      },
      {
        text: `Gerado em: ${new Date().toLocaleDateString(locale)} às ${new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`,
        fontSize: 10,
        color: '#666',
        margin: [0, 0, 0, 15],
        alignment: 'center',
      },
    ]

    // Build table rows: header + data rows
    const tableBody: any[][] = [
      [
        { text: 'Tipo', bold: true, fillColor: '#f0f0f0' },
        { text: 'Descrição', bold: true, fillColor: '#f0f0f0' },
        { text: 'Pessoa', bold: true, fillColor: '#f0f0f0' },
        { text: 'Valor', bold: true, fillColor: '#f0f0f0' },
        { text: 'Vencimento', bold: true, fillColor: '#f0f0f0' },
        { text: 'Chave PIX / Detalhes', bold: true, fillColor: '#f0f0f0' },
      ],
    ]

    // Buscar chaves PIX do banco para todas as payables
    const partyIds = entries
      .filter(e => e.entry_type === 'payable')
      .map(e => e.party_id)
      .filter(Boolean)
    
    const partyPixMap: Record<string, string> = {}
    if (partyIds.length > 0) {
      console.log('[generateAccountsPDF] Buscando chaves PIX de parties...')
      const { data: parties } = await supabase
        .from('parties')
        .select('id, pix_key')
        .in('id', [...new Set(partyIds)])
      
      if (parties) {
        for (const p of parties) {
          if (p.pix_key) partyPixMap[p.id] = p.pix_key
        }
      }
    }

    for (const entry of entries) {
      const type = entry.entry_type === 'receivable' ? 'REC' : 'PAG'
      const description = entry.description || '—'
      const person = entry.party_name_snapshot || '—'
      const amount = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(entry.amount)
      const [year, month, day] = entry.due_date.split('-')
      const dueDate = new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(locale)
      
      let pixOrDetails = ''
      // Para payables, busca chave PIX primeiro do banco, depois do snapshot
      if (entry.entry_type === 'payable') {
        if (entry.party_id && partyPixMap[entry.party_id]) {
          pixOrDetails = partyPixMap[entry.party_id]
        } else if (entry.party_pix_key_snapshot) {
          pixOrDetails = entry.party_pix_key_snapshot
        }
      } else if (entry.payment_method_snapshot) {
        // Para receivables, usa o payment_method
        pixOrDetails = entry.payment_method_snapshot.toUpperCase()
      }

      // Adiciona ícone de cópia se houver chave PIX
      const pixCell = pixOrDetails ? [
        { text: '📋 ', fontSize: 9, color: '#0066cc' },
        { text: pixOrDetails, fontSize: 9, color: '#0066cc', background: '#f0f8ff' }
      ] : { text: '—', fontSize: 9, color: '#999' }

      // Adiciona ícone de cópia no valor também
      const valueCell = [
        { text: '📋 ', fontSize: 9, color: '#28a745' },
        { text: amount, fontSize: 9, bold: true, color: '#28a745', background: '#f0fff4' }
      ]

      tableBody.push([
        { text: type, fontSize: 9 },
        { text: description, fontSize: 9 },
        { text: person, fontSize: 9 },
        { stack: valueCell },
        { text: dueDate, fontSize: 9 },
        { stack: Array.isArray(pixCell) ? pixCell : [pixCell] }
      ])
    }

    docContent.push({
      table: {
        headerRows: 1,
        widths: ['5%', '20%', '20%', '13%', '15%', '27%'],
        body: tableBody,
      },
      layout: {
        fillColor: (rowIndex: number, node: any, columnIndex: number) => {
          return rowIndex === 0 ? '#f0f0f0' : rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9'
        },
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: '#ddd',
        vLineColor: '#ddd',
      },
      margin: [0, 10],
    })

    // Add footer with copy instructions
    docContent.push({
      text: '📋 Azul = Chave PIX (selecione e copie para a área de transferência)',
      fontSize: 9,
      color: '#0066cc',
      margin: [0, 15, 0, 3],
      bold: true,
    })
    docContent.push({
      text: '📋 Verde = Valores (selecione e copie para a área de transferência)',
      fontSize: 9,
      color: '#28a745',
      margin: [0, 0, 0, 0],
      bold: true,
    })

    console.log('[generateAccountsPDF] Criando PDF...')
    const pdf = (pdfMake as any).createPdf({ content: docContent })
    const timestamp = new Date().toISOString().split('T')[0]
    const fileName = `contas-${timestamp}.pdf`

    // Fazer download do PDF
    console.log('[generateAccountsPDF] Fazendo download do PDF...')
    pdf.download(fileName)
    console.log('[generateAccountsPDF] PDF baixado com sucesso!')
  } catch (err) {
    console.error('[generateAccountsPDF] Error generating PDF:', err)
    throw err
  }
}

// (closing brace inserted to balance earlier block)

// =============================================================================
// Receipt attachment
// =============================================================================

const RECEIPT_BUCKET = 'receipts'
const RECEIPT_MAX_BYTES = 5 * 1024 * 1024 // 5 MB
const RECEIPT_ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]

export interface ReceiptUploadResult {
  path: string   // storage path relative to bucket
  url:  string   // signed URL valid for 1 hour
}

/**
 * Upload a receipt file for a financial entry.
 * Returns the storage path stored in financial_entries.receipt_url.
 */
export async function uploadReceipt(
  unitId: string,
  entryId: string,
  file: File,
): Promise<ReceiptUploadResult> {
  if (file.size > RECEIPT_MAX_BYTES) {
    throw new Error(`O arquivo excede o limite de 5 MB.`)
  }
  if (!RECEIPT_ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`Tipo de arquivo não permitido. Use PDF ou imagem (JPEG, PNG, WEBP).`)
  }

  const ext = file.name.split('.').pop() ?? 'bin'
  const path = `${unitId}/${entryId}/receipt.${ext}`

  // Remove old file if it exists (upsert)
  await supabase.storage.from(RECEIPT_BUCKET).remove([path])

  const { error: uploadError } = await supabase.storage
    .from(RECEIPT_BUCKET)
    .upload(path, file, { upsert: true })

  if (uploadError) throw uploadError

  // Save path on the entry row
  const { error: updateError } = await supabase
    .from('financial_entries')
    .update({ receipt_url: path })
    .eq('id', entryId)

  if (updateError) throw updateError

  const { data: signedData } = await supabase.storage
    .from(RECEIPT_BUCKET)
    .createSignedUrl(path, 3600)

  return { path, url: signedData?.signedUrl ?? '' }
}

/**
 * Get a short-lived signed URL to view/download the receipt.
 */
export async function getReceiptUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(RECEIPT_BUCKET)
    .createSignedUrl(path, 3600)

  if (error) throw error
  return data.signedUrl
}

/**
 * Remove the receipt file from storage and clear receipt_url on the entry.
 */
export async function deleteReceipt(entryId: string, path: string): Promise<void> {
  const { error: storageError } = await supabase.storage
    .from(RECEIPT_BUCKET)
    .remove([path])

  if (storageError) throw storageError

  const { error: updateError } = await supabase
    .from('financial_entries')
    .update({ receipt_url: null })
    .eq('id', entryId)

  if (updateError) throw updateError
}

// Party documents attachment
// =============================================================================

const PARTY_DOCUMENTS_BUCKET = 'party-documents'
const PARTY_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024 // 10 MB
const PARTY_DOCUMENT_ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export interface PartyDocument {
  id: string
  party_id: string
  unit_id: string
  document_url: string
  document_name: string
  created_at: string
  created_by: string | null
  updated_at: string
}

export interface PartyDocumentUploadResult {
  id: string
  document_name: string
  document_url: string
  url: string   // signed URL valid for 1 hour
}

/**
 * Upload a document file for a party/contact.
 * Creates a record in party_documents table.
 */
export async function uploadPartyDocument(
  unitId: string,
  partyId: string,
  file: File,
  documentName?: string,
): Promise<PartyDocumentUploadResult> {
  if (file.size > PARTY_DOCUMENT_MAX_BYTES) {
    throw new Error(`O arquivo excede o limite de 10 MB.`)
  }
  if (!PARTY_DOCUMENT_ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`Tipo de arquivo não permitido. Use PDF, imagem, Word ou Excel.`)
  }

  // Create unique filename with timestamp to avoid collisions
  const timestamp = Date.now()
  const ext = file.name.split('.').pop() ?? 'bin'
  const path = `${unitId}/${partyId}/${timestamp}-document.${ext}`

  // Upload file to storage
  const { error: uploadError } = await supabase.storage
    .from(PARTY_DOCUMENTS_BUCKET)
    .upload(path, file)

  if (uploadError) throw uploadError

  // Get current user
  const { data: { user } } = await supabase.auth.getUser()
  console.log('[uploadPartyDocument] Current user:', user?.id)

  // Create record in party_documents table
  const { data: docRecord, error: dbError } = await supabase
    .from('party_documents')
    .insert({
      party_id: partyId,
      unit_id: unitId,
      document_url: path,
      document_name: documentName || file.name,
      created_by: user?.id,
    })
    .select()
    .single()

  if (dbError) {
    // Rollback: delete uploaded file if DB insert fails
    await supabase.storage.from(PARTY_DOCUMENTS_BUCKET).remove([path])
    console.error('[uploadPartyDocument] DB insert error:', dbError)
    throw new Error(`Erro ao salvar documento: ${dbError.message}`)
  }

  console.log('[uploadPartyDocument] Document created:', docRecord.id)

  // Generate signed URL
  const { data: signedData } = await supabase.storage
    .from(PARTY_DOCUMENTS_BUCKET)
    .createSignedUrl(path, 3600)

  return {
    id: docRecord.id,
    document_name: docRecord.document_name,
    document_url: path,
    url: signedData?.signedUrl ?? '',
  }
}

/**
 * Get all documents for a party/contact.
 */
export async function getPartyDocuments(partyId: string): Promise<PartyDocument[]> {
  console.log('[getPartyDocuments] Fetching documents for party:', partyId)
  const { data, error } = await supabase
    .from('party_documents')
    .select('*')
    .eq('party_id', partyId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('[getPartyDocuments] Error:', error)
    throw error
  }
  console.log('[getPartyDocuments] Loaded documents:', data?.length || 0)
  return data || []
}

/**
 * Get a short-lived signed URL for a document.
 */
export async function getPartyDocumentUrl(documentPath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(PARTY_DOCUMENTS_BUCKET)
    .createSignedUrl(documentPath, 3600)

  if (error) throw error
  return data.signedUrl
}

/**
 * Update document name.
 */
export async function updatePartyDocumentName(
  documentId: string,
  newName: string,
): Promise<PartyDocument> {
  const { data, error } = await supabase
    .from('party_documents')
    .update({
      document_name: newName,
      updated_at: new Date().toISOString(),
    })
    .eq('id', documentId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a document (removes file and DB record).
 */
export async function deletePartyDocument(documentId: string, documentPath: string): Promise<void> {
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(PARTY_DOCUMENTS_BUCKET)
    .remove([documentPath])

  if (storageError) throw storageError

  // Delete DB record
  const { error: dbError } = await supabase
    .from('party_documents')
    .delete()
    .eq('id', documentId)

  if (dbError) throw dbError
}


